import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import type Job from '../../../../models/job.js';
import definition from '@job-schemas/public-ledger/autopay-fee-over-cap.json';
import { jobSchema } from '../../job-schema.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';
import {
  COMPLETE_COVERED,
  DONT_PAY,
  FEE_OVER_CAP_TEXT,
  PAY_THIS_FEE,
  feeOverCap,
  feeOverCapCoveredText,
  feeOverCapIntro,
  feeOverCapRows,
} from './autopay-fee-over-cap-logic.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';

/**
 * The PUBLIC_LEDGER team's card when a company has had this month's 5 public ledger fee top-ups paid by nodeStream and a
 * change needs one more (#25 phase 6, round 6): a change of amounts (before its signatures, or at its last one) or a
 * change of the automatic payment card. public-ledger/autopay/seed-fee raises it in the nodeStream company, one open per
 * waiting change, and keeps its figures up to date. Nothing skips the limit: the change waits for staff. Pay this fee
 * sends one top-up past the limit and the change goes ahead; Don't pay cancels the change and tells the requester.
 * Round 7: once the company's own operating account can pay the fee, the change goes ahead on the company's money. Round
 * 10: the card is not closed for it: the server marks it (COVERED), the card says why it was raised and why it's no longer
 * needed, and offers only Complete (outcome covered), which ends it with no money effect. Round 11: job/complete refuses
 * Complete on a card not marked covered, and the card stays open. A Pay this fee or Don't pay pressed before the card updated is
 * harmless (the server sends or cancels nothing for a covered change).
 */
@customElement(NSPublicLedgerAutopayFeeOverCap.is)
export default class NSPublicLedgerAutopayFeeOverCap extends NSJobComponent {
  static get is() {
    return 'ns-public-ledger-autopay-fee-over-cap';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { pay: { type: 'hidden' }, decline: { type: 'hidden' }, covered: { type: 'hidden' } },
      icon: 'transaction',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  @state()
  private __busy = false;

  // The server keeps the card's metadata up to date (its figures and COVERED) and the container doesn't pass its job on: the card follows
  // the job itself, and each update re-renders its buttons (updated() tells the container)
  async connectedCallback() {
    await super.connectedCallback();
    const jobId = this._job?.id;
    if (!jobId || !this.isConnected) return;
    this._subscriptions.subscribeDoc<Job>('job', jobId, (updated) => {
      this._job = updated;
      this.requestUpdate();
    });
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __decide(outcome: 'pay' | 'decline' | 'covered') {
    if (this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await this.outcome(outcome);
    } catch (err) {
      // Approved (round 4 follow-ups, small string 2)
      this.showError(err, 'This job could not be completed. Please try again.');
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const fee = feeOverCap(this._job?.metadata);
    return html`
      <ns-job-layout>
        <p slot="intro">${feeOverCapIntro(fee.companyName)}</p>
        <div class="job-panel">
          <ns-detail-list .rows=${feeOverCapRows(fee)}></ns-detail-list>
          ${fee.covered
            ? html`
                <div class="note" role="status">
                  ${feeOverCapCoveredText(fee).map(
                    (reason) => html`
                      <p>
                        <strong>${reason.heading}</strong>
                        ${reason.text}
                      </p>
                    `,
                  )}
                </div>
              `
            : html`
                <p>${FEE_OVER_CAP_TEXT}</p>
              `}
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    if (feeOverCap(job.metadata).covered) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__decide('covered')}>
          ${COMPLETE_COVERED}
        </wa-button>
      `;
    }
    return html`
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${this.__busy}
        @click=${() => this.__decide('decline')}
      >
        ${DONT_PAY}
      </wa-button>
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__decide('pay')}>
        ${PAY_THIS_FEE}
      </wa-button>
    `;
  }
}
